<script lang="ts">
	//Services
	import { postReq } from 'src/services/http';
	//Helpers
	import { devices_config } from '@devices/index';
	import { get_mode } from 'src/helpers/interface_helpers';
	import { onSettingsResponse } from 'src/helpers/device_settings_helpers';
	import { requestEditDevice } from 'src/components/Lists/list-helpers';
	//Stores
	import { devices_status, devices_settings } from 'src/stores/device-runtime/status';
	import { user_data } from 'src/stores/account/user';
	//Types
	import type { Device } from 'src/types/Device';
	import { NotifType } from 'src/types/DeviceNotifPermissions';
	import { onMount } from 'svelte';
	import { testNotifications } from 'src/types/DeviceNotifPermissions';
	//Components
	import PushMailPermissions from './PushMailPermissions.svelte';
	import NonPremiumUserAlert from './NonPremiumUserAlert.svelte';

	export let device: Device;

	const settings = devices_settings.getByRealId(device.id);
	const status = devices_status.getByRealId(device.id);
	const mode = get_mode(device, status, settings);
	const isPremiumUser = $user_data?.premium > 0;
	const notification = 'offline';

	// Notifications in production
	const notifications =
		devices_config[device.type].modes[mode]?.notifications?.filter(
			(notification) => /offline/.test(notification) && testNotifications.includes(notification),
		) || [];

	onMount(() => {
		const isMissingNotifPermissions = device?.notif_permissions === undefined;
		const isMissingOfflinePermissions = device?.notif_permissions?.[notification] === undefined;

		if (!isMissingNotifPermissions && !isMissingOfflinePermissions) {
			return;
		}

		// Ensure that device.notif_permissions is defined
		if (isMissingNotifPermissions) {
			device.notif_permissions = {};
		}

		// Ensure that device.notif_permissions[notification] is defined
		for (const notification of notifications) {
			if (device.notif_permissions[notification]) {
				continue;
			}

			device.notif_permissions[notification] = [1, 1];
		}

		requestEditDevice(device);
	});

	// Used to bind the toggles to the device.notif_permissions object
	$: notifPermissionsBool =
		typeof device?.notif_permissions !== 'undefined'
			? Object.keys(device.notif_permissions)?.reduce((acc, notification) => {
					return {
						...acc,
						[notification]: {
							[NotifType.MAIL]:
								device.notif_permissions[notification][NotifType.MAIL] === 1 ||
								device.notif_permissions[notification][NotifType.MAIL],
							[NotifType.PUSH]:
								device.notif_permissions[notification][NotifType.PUSH] === 1 ||
								device.notif_permissions[notification][NotifType.PUSH],
						},
					};
				}, {})
			: {};

	const changeNotificationSetting = async (e) => {
		let notification = e.detail.notification;
		let type = e.detail.notifType;
		device.notif_permissions[notification][type] = device.notif_permissions[notification][type]
			? 0
			: 1;

		const res = await postReq('interface/device/set_device_notifications', {
			id: device.id,
			set: notification,
			val: device.notif_permissions[notification][type],
			ntype: type,
		});

		if (res.isok) {
			requestEditDevice(device);
		}
		onSettingsResponse(res, device);
	};
</script>

{#if isPremiumUser}
	{#if device?.notif_permissions}
		{#if notifPermissionsBool?.[notification]}
			<PushMailPermissions
				mail={notifPermissionsBool[notification][NotifType.MAIL]}
				push={notifPermissionsBool[notification][NotifType.PUSH]}
				{notification}
				loading={false}
				{notifPermissionsBool}
				on:changeNotificationSetting={changeNotificationSetting}
			/>
		{/if}
	{/if}
{:else}
	<NonPremiumUserAlert {device} />
{/if}
